import {
    VerifyOTPRequest,
    AuthResponse,
    TOTPSetupRequest,
    TOTPSetupResponse,
    TOTPVerifySetupRequest,
    TOTPVerifySetupResponse,
    TOTPDisableRequest,
    TOTPStatusResponse,
    CheckUserResponse,
    OTPResponse,
    LoginApiResponse,
    LoginRequest
} from "@/types/auth";
import { RequestAccessData, TeamApiResponse } from "@/types/requestAccess";
import { request } from "./network";
import { buildPermissions } from "@/utils/permissionBuilder";
import { rpcApi, type RPCResponse } from "./rpc";

interface BaseRPCResult {
    message?: string;
    msg?: string;
    success?: boolean;
    [key: string]: unknown;
}

const mapRpcMessage = (response: RPCResponse<BaseRPCResult>): string => {
    if (response.error) {
        if (typeof response.error === "string") return response.error;
        if (typeof response.error === "object") return response.error.message;
    }
    const result = response.result || {};
    return (
        result.message || 
        result.msg || 
        response.message || 
        response.msg || 
        ""
    );
};

const mapRpcCode = (response: RPCResponse<unknown>, fallback = 200): number => {
    if (response.error && typeof response.error === "object") {
        return response.error.code ?? fallback;
    }
    return response.code ?? fallback;
};

export const authApi = {
    checkUser: async (email: string): Promise<CheckUserResponse> => {
        return request('POST', 'check', { email });
    },

    sendOTP: async (email: string): Promise<OTPResponse> => {
        return request('POST', 'otp', { email });
    },

    resendOTP: async (email: string): Promise<OTPResponse> => {
        return request('POST', 'resend_otp', { email });
    },

    googleLogin: async (jwt: string, redirect?: string, totpCode?: string, service?: string): Promise<LoginApiResponse> => {
        const payload: LoginRequest = {
            type: 1,
            jwt,
            redirect: redirect || "/home",
            service
        };
        if (totpCode) {
            payload.totp_code = totpCode;
        }
        return request('POST', '/login', payload);
    },

    microsoftLogin: async (accessToken: string, redirect?: string, totpCode?: string, service?: string): Promise<LoginApiResponse> => {
        const payload: LoginRequest = {
            type: 2,
            accessToken,
            redirect: redirect || "/home",
            service
        };
        if (totpCode) {
            payload.totp_code = totpCode;
        }
        return request('POST', '/login', payload);
    },

    verifyOTP: async (data: VerifyOTPRequest): Promise<AuthResponse> => {
        const payload: LoginRequest = {
            type: 3,
            redirect: data.redirect,
            service: data.service
        };

        if (data.email) payload.email = data.email;
        if (data.emailOtp) payload.otp = parseInt(data.emailOtp, 10);
        if (data.totpCode) payload.totp_code = data.totpCode;

        try {
            const response = await request<LoginApiResponse>('POST', '/login', payload);

            if (response.data?.totpRequired || response.data?.requiresTotp) {
                return {
                    success: true,
                    message: "TOTP verification required",
                    totpRequired: true,
                    requiresTotp: true,
                    data: response.data
                };
            }

            return {
                success: response.success || response.code === 200 || true,
                message: response.message || response.msg || "Login successful!",
                token: response.data?.token,
                data: response.data
            };
        } catch (error) {
            if (error instanceof Error) {
                return {
                    success: false,
                    message: error.message || "Verification failed. Please try again."
                };
            }
            throw error;
        }
    },

    getTeams: async (): Promise<TeamApiResponse> => {
        return request('GET', 'team');
    },

    requestAccess: async (data: RequestAccessData): Promise<AuthResponse> => {
        const teamId = data.defaultTeam || (Array.isArray(data.teams) ? data.teams[0] : data.teams) || "";
        
        const permissions = buildPermissions({
            business: data.business,
            team: teamId,
            platforms: data.platforms || {},
            accessLevel: "w",
        });
        
        const response = await request<LoginApiResponse>('POST', 'register', {
            name: data.fullName,
            email: data.email.toLowerCase().trim(),
            phone: data.mobile || "",
            business: data.business,
            team: teamId,
            permissions,
        });
        
        return {
            success: response.code === 200 || !!response.data,
            message: response.message || response.msg || (response.code === 200 ? "Request sent successfully!" : "Failed to send request"),
            data: response.data,
        };
    },

    generateTOTPSecret: async (data?: TOTPSetupRequest): Promise<TOTPSetupResponse> => {
        interface TOTPSetupResult extends BaseRPCResult {
            secret?: string;
            qr?: string;
            qrCode?: string;
            qrcode?: string;
            qr_url?: string;
            data?: {
                secret?: string;
                qrCode?: string;
                qr?: string;
            };
        }
        const response = await rpcApi.call<TOTPSetupResult>("auth.totp_setup", data || {});
        const message = mapRpcMessage(response);
        const result = response.result || {};

        return {
            success: !response.error,
            code: mapRpcCode(response),
            message,
            msg: message,
            data: {
                secret: result.secret || result.data?.secret || "",
                qrCode: (result.qr || result.qrCode || result.qrcode || result.qr_url || result.data?.qrCode || result.data?.qr || "") as string,
            },
        };
    },

    verifyTOTPSetup: async (data: TOTPVerifySetupRequest): Promise<TOTPVerifySetupResponse> => {
        data.enable = true;
        const response = await rpcApi.call<BaseRPCResult>("auth.totp_verify", { ...data, enable: true });
        const message = mapRpcMessage(response);
        const result = response.result || {};
        const success = (result.success as boolean | undefined) ?? !response.error;

        return {
            success,
            code: mapRpcCode(response, success ? 200 : 400),
            message,
            msg: message,
        };
    },

    disableTOTP: async (data?: TOTPDisableRequest): Promise<TOTPStatusResponse> => {
        interface TOTPDisableResult extends BaseRPCResult {
            totpEnabled?: boolean;
            enabled?: boolean;
            isEnabled?: boolean;
            status?: boolean | number;
        }
        const response = await rpcApi.call<TOTPDisableResult>("auth.totp_disable", data || {});
        const message = mapRpcMessage(response);
        const result = response.result || {};
        const success = (result.success as boolean | undefined) ?? !response.error;
        const enabled = result.totpEnabled ?? result.enabled ?? result.isEnabled ?? result.status;

        return {
            success,
            code: mapRpcCode(response, success ? 200 : 400),
            message,
            msg: message,
            data: {
                totpEnabled: Boolean(enabled),
            },
        };
    },

    getTOTPStatus: async (): Promise<TOTPStatusResponse> => {
        interface TOTPStatusResult extends BaseRPCResult {
            totpEnabled?: boolean;
            enabled?: boolean;
            isEnabled?: boolean;
            status?: boolean | number;
        }
        const response = await rpcApi.call<TOTPStatusResult>("auth.totp_status", {});
        const message = mapRpcMessage(response);
        const result = response.result || {};
        const success = (result.success as boolean | undefined) ?? !response.error;
        const enabled = result.totpEnabled ?? result.enabled ?? result.isEnabled ?? result.status;

        return {
            success,
            code: mapRpcCode(response, success ? 200 : 400),
            message,
            msg: message,
            data: {
                totpEnabled: Boolean(enabled),
            },
        };
    },

};
